import browser from '@/lib/browser'

export const STANDARD_TRIAL_PARAM = 'request_trial'
export const HUNT_ELITE_TRIAL_PARAM = 'hunt_elite_trial'

// FUTURE - Core to clean up this file and remove dead code

/**
 * Functions to handle urls and url parameters
 *
 * Parameters supported:
 *
 *    product=xxxxx&state=xx - Product and state (optional) to be purchased. Carries through login.
 *    after_login=xxxx - URL encoded destination to go to after logging in.
 *    password_reset=true - Show the reset my password dialog on the login page.
 *    token=xxxx#reset - Password reset token which is sent out via email.
 *    already_owns - Shows a dialog stating the customer already owns premium.
 *    promo=xxxx - Promo code to be applied to a purchase
 *    request_trial=true - Requests a new trial for the user (API determines if they get one or not)
 *    inline_membership=true - Overrides the change membership dialog with the inline change
 *      membership area
 *    _branch_match_id - Branch.io param which is used by their web sdk to look up share info
 *    email=xxxx@xxxx - Email of user coming from email marketing campaign
 *    mode=xxx - Force a particular mode on load; primarily for use when deeplinking to a query and a certain
 *      mode needs to be enabled for the correct content to be queried
 *    id=xxxx - A feature ID that can be used to query as a more accurate alternative to querying
 *      with a lat/lon
 *    cards - which non-navigational cards (e.g. control-layers panel/legend/layer-detail) are open
 *    link_id - identifies the navigation as originating from a share link & helpful to track organic sharing
 */
class Urls {
  /**
   * Returns a list of the supported parameter keys. Used to differentiate webmap parameters that
   * need encoding from other params, eg. marketing parameters
   */
  getSupportedParams(): string[] {
    /** Params that are supported in any vertical/context */
    const universalSupportedParams = [
      /** Used to identify share links */
      'link_id',
    ]
    return universalSupportedParams
  }

  /**
   * Removes any parameters present in the url
   */
  removeParams(): void {
    window.history.replaceState(null, '', browser.getLocation().pathname)
  }

  /**
   * Remove an individual parameter from the url
   *
   * @param toRemove - Name of parameter to remove from the url
   */
  removeParam(toRemove: string): void {
    const currentLocation = browser.getLocation()
    const urlParams = new URLSearchParams(currentLocation.search)

    urlParams.delete(toRemove)
    const newParamString = urlParams.toString()

    if (newParamString) {
      window.history.replaceState(
        null,
        '',
        currentLocation.pathname + '?' + urlParams.toString()
      )
    } else {
      window.history.replaceState(null, '', currentLocation.pathname)
    }
  }

  /**
   * Add an individual parameter to the url
   *
   * @param key - Name of parameter to add
   * @param value - value of parameter to add
   */
  addParam(key: string, value: string): void {
    const currentLocation = browser.getLocation()
    const urlParams = new URLSearchParams(currentLocation.search)

    urlParams.set(key, value)
    window.history.replaceState(
      null,
      '',
      currentLocation.pathname + '?' + urlParams.toString()
    )
  }

  /**
   * Add key value pairs from object to url params
   *
   * @param paramsObj - object with key value pairs that represent params
   */
  addParamsFromObject(paramsObj: Record<string, string>): void {
    for (const key in paramsObj) {
      this.addParam(key, paramsObj[key])
    }
  }

  /**
   * Return url params as an object
   *
   * @returns object with key value pairs of query params
   */
  getFullParamsObject(): Record<string, string> {
    const currentLocation = browser.getLocation()
    const urlParams = new URLSearchParams(currentLocation.search)

    return Object.fromEntries(urlParams)
  }

  /**
   * Return all url params
   *
   * @return full query string with all params
   */
  getFullParamString(): string {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    return urlParams.toString()
  }

  /**
   * Return all supported url params as a string
   *
   * @return full query string with all supported params
   */
  getSupportedParamsString(): string {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const supportedParams = new URLSearchParams()

    for (const param of urlParams.entries()) {
      if (this.getSupportedParams().includes(param[0])) {
        supportedParams.append(param[0], param[1])
      }
    }

    return supportedParams.toString()
  }

  /**
   * Returns whether there is a sharing parameter present or not
   *
   * @return - True if _branch_match_id or share_id is present
   */
  hasSharingParam(): string | null {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const branchID = urlParams.get('_branch_match_id')
    const shareID = urlParams.get('share_id')

    return branchID || shareID
  }

  /**
   * Return the share ID. This will either come directly from the URL or from
   * parsing the Branch.io response
   *
   * @return share_id parameter value
   */
  getShareID(): string | null {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    let shareID = urlParams.get('share_id')

    // if no share ID url param is found, check for branch share
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = window as any
    if (!shareID && win.branchShareID !== undefined) {
      shareID = win.branchShareID
    }
    return shareID
  }

  /**
   * Returns the redemption code parameter value from the url
   *
   * @return redemption parameter value
   */
  getRedemptionCodeParam(): string | null {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const redemption = urlParams.get('redemption_code')

    return redemption
  }

  /**
   * Returns the after_login parameter value from the url
   *
   * @return after_login parameter value
   */
  getAfterLoginParam(): string | undefined {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    return urlParams.get('after_login') ?? undefined
  }

  /**
   * Returns the inline_membership parameter value from the url
   *
   * @return inline_membership parameter value
   */
  getInlineMembershipParam(): boolean {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const inline = urlParams.get('inline_membership')

    return inline === 'true'
  }

  /**
   * Returns the product parameter value from the url
   *
   * @return product parameter value
   */
  getProductParam(): string | null {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const product = urlParams.get('product')

    return product
  }

  /**
   * Returns the state parameter value from the url
   *
   * @return state parameter value
   */
  getStateParam(): string | null {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const state = urlParams.get('state')

    return state
  }

  /**
   * Returns the full product and state url parameter string. To be used
   * during account creation.
   *
   * @return full query string with product and state parameters
   */
  getFullProductParamString(): string {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const product = urlParams.get('product')
    const state = urlParams.get('state')

    let combinedParams = ''

    if (product) {
      combinedParams = 'product=' + product

      if (state) {
        combinedParams += '&state=' + state
      }
    }

    return combinedParams
  }

  /**
   * Returns the already_owns parameter value from the url
   *
   * @return already_owns parameter value
   */
  getAlreadyOwnsParam(): string | null {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    return urlParams.get('already_owns')
  }

  /**
   * Returns the promo parameter value from the url
   *
   * @return promo parameter value
   */
  getPromoParam(): string | null {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const promo = urlParams.get('promo')

    return promo
  }

  /**
   * Returns the request_trial param value from the url
   *
   * @return request_trial parameter value
   */
  getTrialParam(): string | null {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const newTrial = urlParams.get(STANDARD_TRIAL_PARAM)

    return newTrial
  }

  /**
   * Returns the request_trial param value from the url
   *
   * @return request_trial parameter value
   */
  getEliteTrialParam(): string | null {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const newEliteTrial = urlParams.get(HUNT_ELITE_TRIAL_PARAM)

    return newEliteTrial
  }

  /**
   * Returns the email param value from the url
   *
   * @return email parameter value
   */
  getEmailParam(): string | undefined {
    return (
      new URLSearchParams(browser.getLocation().search).get('email') ??
      undefined
    )
  }

  /**
   * Returns the app param value from the url
   *
   * @return app parameter value
   */
  getAppParam(): string | null {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const app = urlParams.get('app')

    return app
  }

  /**
   * Returns the pro_deal query param from the url
   *
   * @return pro_deal parameter value converted to strict boolean
   */
  getEliteProDealParam(): string | null {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const proDealUrl = urlParams.get('pro_deal')

    return proDealUrl
  }

  /**
   * Returns the mode param from the url
   *
   * @return mode param value or empty if not present
   */
  getModeParam(): string {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const query = urlParams.get('mode')

    return query || ''
  }

  /**
   * Returns the id param from the url. Used specifically for querying a feature
   *
   * @return id value or empty if not present
   */
  getIdParam(): string {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const query = urlParams.get('id')

    return query || ''
  }

  /**
   * Returns the 3d param from the url. Used specifically for toggling the map pitch
   *
   * @return 3d value or empty if not present
   */
  get3dParam(): string {
    const urlParams = new URLSearchParams(browser.getLocation().search)
    const query = urlParams.get('3d')

    return query || ''
  }
}

const urls = new Urls()
export default urls

/**
 * Is the given string a url that matches common schemes protocols or some domains
 * @param str - String to test
 * @return
 */
export function isUrl(str: string): boolean {
  const schemes =
    /^http|^geo|^im|^irc|^mailto|^mms|^news|^nntp|^sms|^ssh|^tel|^urn|^webcal|^xmpp|^wtai|^sip|^magnet/g
  const domains = /\.com$|\.co$|\.edu$|\.org$|\.net$|\.gov$|\.me$/g
  return schemes.test(str) || domains.test(str)
}

/**
 * This adds a maxwidth url paramater to a Cloudinary image url
 * Docs https://cloudinary.com/documentation/image_transformation_reference#width_parameter
 * @param url - Cloundinary url
 * @param [maxWidth=1600] - Max width of image
 * @return Modified url
 */
export const resizeCloudinaryImageUrl = (
  url: string,
  maxWidth = 1600
): string => {
  const cloudinaryParams = `q_auto,f_auto,w_${maxWidth}`

  return url.replace(
    /\/image\/upload\/(?:(.*?)\/+)?v(\d+)/,
    `/image/upload/${cloudinaryParams}/v$2`
  )
}
